import { createAsyncThunk } from '@reduxjs/toolkit';
import { updateAuthTokens } from 'utils/cookies';
import { API, parseErrorData } from 'utils/service';
import { type ICurrentUserProfileResponse } from '__generated__/api';
import { type AppAsyncThunkConfig } from '../index';
import { NAMESPACE } from './helpers';
import { getAuthTokens } from 'utils/cookies';
import { RefreshTokenInput, AuthStatus } from 'services/auth';

export const actionAccountGetUser = createAsyncThunk<
  ICurrentUserProfileResponse,
  { isOnTheBackground: boolean },
  AppAsyncThunkConfig
>(`ACCOUNT/actionAccountGetUser`, async () => {
  try {
    const { data } = await API.api.accountGetCurrentAppUserList();
    return data;
  } catch (e: any) {
    throw parseErrorData(e);
  }
});
export const actionAccountRefreshToken = createAsyncThunk<
  void,
  RefreshTokenInput,
  AppAsyncThunkConfig
>(`${NAMESPACE}/actionAccountRefreshToken`, async (input, { dispatch }) => {
  try {
    const { data } = await API.api.authRefreshTokenCreate(input);
    const { expires } = getAuthTokens();

    updateAuthTokens({
      token: data?.token ?? undefined,
      refreshToken: data?.refreshToken ?? undefined,
      expires: expires ?? undefined,
    });

    const { data: checkAuthData } = await API.api.authCheckAuthList();

    if (checkAuthData === AuthStatus.ERROR) {
      updateAuthTokens();
    }

    if (checkAuthData === AuthStatus.SUCCESS) {
      await dispatch(actionAccountGetUser({ isOnTheBackground: false }));
    }
  } catch (e: unknown) {
    updateAuthTokens();
    throw parseErrorData(e as Partial<Error>);
  }
});

export const actionAccountCheckAuth = createAsyncThunk<
  AuthStatus,
  void,
  AppAsyncThunkConfig
>(`${NAMESPACE}/actionAccountCheckAuth`, async (_, { dispatch }) => {
  try {
    const { data } = await API.api.authCheckAuthList();

    if (data === AuthStatus.SUCCESS) {
      await dispatch(actionAccountGetUser({ isOnTheBackground: false }));
    }

    const { token, refreshToken } = getAuthTokens();

    if (data === AuthStatus.ERROR && token && refreshToken) {
      await dispatch(actionAccountRefreshToken({ token, refreshToken }));
    }

    return data as AuthStatus;
  } catch (e: any) {
    throw parseErrorData(e);
  }
});

export const actionAccountGeneratePassword = createAsyncThunk<
  void,
  { email: string },
  AppAsyncThunkConfig
>(`ACCOUNT/actionAccountGeneratePassword`, async (data) => {
  try {
    await API.api.authGeneratePasswordCreate(data);
  } catch (e: any) {
    throw parseErrorData(e);
  }
});
export const actionAccountSedCode = createAsyncThunk<
  void,
  { email: string; code: string },
  AppAsyncThunkConfig
>(`ACCOUNT/actionAccountSedCode`, async (data, { dispatch }) => {
  try {
    const { data: res } = await API.api.authLoginWithCodeCreate(data);
    updateAuthTokens({
      token: res?.jwt?.token || '',
      refreshToken: res?.jwt?.refreshToken || '',
      expires: res?.expires ? new Date(res.expires).toUTCString() : null,
    });
    const getUserResult = await dispatch(actionAccountGetUser({ isOnTheBackground: false }));

    if (actionAccountGetUser.rejected.match(getUserResult)) {
      throw getUserResult.error;
    }
  } catch (e: any) {
    throw parseErrorData(e);
  }
});
export const actionAccountLogout = createAsyncThunk<void, void, AppAsyncThunkConfig>(
  `ACCOUNT/actionAccountLogout`,
  async () => {
    try {
      await API.api.authLogoutCreate();
      updateAuthTokens();
    } catch (e: any) {
      throw parseErrorData(e);
    }
  },
);
